import {
  parseDateSlashFormatShortToISO,
  parseCoverageLimitsWithK,
} from 'Shared/utils';
import { PriorPolicyChallengeFieldsType, ServerRequestType } from '.';
import { sanitizeString } from '../utils';

// Shared coverage limits mapping
const COVERAGE_LIMIT_MAP: Record<
  string,
  { limitPerPerson: number; limitPerAccident: number }
> = {
  unknown: { limitPerPerson: 0, limitPerAccident: 0 },
  fifteen_thirty: { limitPerPerson: 15000, limitPerAccident: 30000 },
  twenty_five_fifty: { limitPerPerson: 25000, limitPerAccident: 50000 },
  twenty_five_sixty_five: { limitPerPerson: 25000, limitPerAccident: 65000 },
  thirty_sixty: { limitPerPerson: 30000, limitPerAccident: 60000 },
  thirty_sixty_five: { limitPerPerson: 30000, limitPerAccident: 65000 },
  thirty_five_seventy: { limitPerPerson: 35000, limitPerAccident: 70000 },
  fifty_fifty: { limitPerPerson: 50000, limitPerAccident: 50000 },
  fifty_one_hundred: { limitPerPerson: 50000, limitPerAccident: 100000 },
  seventy_five_one_fifty: { limitPerPerson: 75000, limitPerAccident: 150000 },
  one_hundred_two_hundred: {
    limitPerPerson: 100000,
    limitPerAccident: 200000,
  },
  one_hundred_three_hundred: {
    limitPerPerson: 100000,
    limitPerAccident: 300000,
  },
  two_hundred_three_hundred_or_greater: {
    limitPerPerson: 200000,
    limitPerAccident: 300000,
  },
};

const parseCoverageLimits = (
  limitValue: string
): { limitPerPerson: number | null; limitPerAccident: number | null } => {
  // Check if it's a legacy key format first
  if (COVERAGE_LIMIT_MAP[limitValue]) {
    return COVERAGE_LIMIT_MAP[limitValue];
  }

  // Use shared utility for parsing coverage limits with K-suffix support
  return parseCoverageLimitsWithK(limitValue);
};

export const getCoverageLimitKey = (
  limitPerPerson: number,
  limitPerAccident: number | null
): string => {
  // Find the key that matches the given limits
  for (const [key, limits] of Object.entries(COVERAGE_LIMIT_MAP)) {
    if (
      limits.limitPerPerson === limitPerPerson &&
      limits.limitPerAccident === limitPerAccident &&
      limitPerAccident !== null
    ) {
      return key;
    }
  }

  return 'unknown';
};

export const buildCoverage = (
  coverageType: ServerRequestType['coverages'][number]['coverageType'],
  limitValue: string | null | undefined
): ServerRequestType['coverages'][number] => {
  const limits = limitValue
    ? parseCoverageLimits(limitValue)
    : { limitPerPerson: 0, limitPerAccident: 0 };

  return {
    coverageType,
    limitPerPerson: limits.limitPerPerson,
    limitPerAccident: limits.limitPerAccident,
  };
};

const buildCoverages = (
  data: PriorPolicyChallengeFieldsType
): ServerRequestType['coverages'] => {
  return [
    buildCoverage('bodilyInjury', data.biLimit),
    buildCoverage('combinedSingleLimit', data.cslLimit),
    buildCoverage('personalInjuryProtection', data.pipLimit),
  ];
};

export const transformChallengePriorPolicyRequest = (
  data: PriorPolicyChallengeFieldsType
): ServerRequestType => {
  return {
    carrierName: sanitizeString(data.carrierName) ?? '',
    startDate: parseDateSlashFormatShortToISO(data.startDate),
    endDate: parseDateSlashFormatShortToISO(data.endDate),
    amBestNumber: sanitizeString(data.amBestNumber) ?? '',
    state: `US-${sanitizeString(data.state) ?? ''}`,
    coverages: buildCoverages(data),
    challengeReason: sanitizeString(data.challengeReason) ?? '',
    reasonText: sanitizeString(data.reasonText) ?? '',
    policyNumber: sanitizeString(data.policyNumber) ?? '',
  };
};
